import React, { useState, useEffect, useRef } from "react";
import { Line } from "react-chartjs-2";
import Navbar from "../components/Navbar";
import { ref, onValue } from "firebase/database";
import { db } from "../firebase";
import {
  Chart as ChartJS,
  LineElement, PointElement,
  CategoryScale, LinearScale,
  Title, Tooltip,
  Legend,
} from "chart.js";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import {
  Download, Filter, BarChart2, Table2, ChevronLeft,
  ChevronRight, ChevronDown, FileText, FileSpreadsheet
} from "lucide-react";

ChartJS.register(LineElement, PointElement, CategoryScale, LinearScale, Title, Tooltip, Legend);

const SENSOR_KEYS = {
  suhu: {label: "Suhu", unit: "°C", color: "#EF4444"},
  kelembapan: {label: "Kelembapan", unit: "%", color: "#3B82F6"},
  co2: {label: "Gas CO₂", unit: "ppm", color: "#10B981"},
  co: {label: "Asap", unit: "ppm", color: "#6B7280"},
  kebisingan: {label: "Kebisingan", unit: "dB", color: "#8B5CF6"},
  cahaya: {label: "Intensitas Cahaya",unit: "lux", color: "#ffd52c"},
};

function labelBulan(monthStr) {
  if (!monthStr) return "Semua Bulan";
  const [y, m] = monthStr.split("-");
  if (!y || !m) return "Semua Bulan";
  return new Date(Number(y), Number(m) - 1).toLocaleString("id-ID", {
    month: "long", year: "numeric",
  });
}

function prediksiLabel(p) {
  if (p === 2) return {text: "Bahaya", cls: "bg-red-100 text-red-700"};
  if (p === 1) return {text: "Tidak Nyaman", cls: "bg-yellow-100 text-yellow-700"};
  if (p === 0) return {text: "Aman", cls: "bg-green-100 text-green-700"};
  return {text: "-", cls: "bg-gray-100 text-gray-500"};
}

// component 
export default function HistoricalData() {
  const [dataPoints, setDataPoints] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [pilihBulan, setpilihBulan] = useState("");
  const [pilihKelas, setpilihKelas] = useState("Semua Kelas");
  const [pilihSensor,setpilihSensor]= useState("Semua Sensor");
  const [loading, setLoading] = useState(true);
  const [bulan, setBulan] = useState([]);
  const [kelas, setKelas] = useState([]);
  const [page, setPage] = useState(1);
  const [pageInput, setPageInput] = useState(1);
  const [ambangBatas, setAmbangBatas] = useState({});
  const [isLoaded, setIsLoaded] = useState(false);
  const [showMenuDownload, setShowMenuDownload] = useState(false);
  const menuRef = useRef(null);
  const rowsPerPage = 12;
  const rows = (pilihBulan ? filtered : dataPoints);

  // Firebase — historis
  useEffect(() => {
    const unsub = onValue(ref(db, "dataHistoris"), (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const toNumber = (val) => {
          if (val == null) return null;
          if (typeof val === "number") return val;
          if (typeof val === "string") return parseFloat(val);
          if (typeof val === "object") return parseFloat(val.value ?? val.val ?? 0);
          return null;
        };
        
        const list = Object.entries(data).flatMap(([dateKey, dayData]) =>
          Object.values(dayData).map((item) => {
            let prediksi =
              item.prediksi ?? item.prediksiML ?? item.hasilPrediksi ??
              item.ml_prediction ?? item.status_prediksi ?? item.status_akhir ?? null;
            if (Array.isArray(prediksi))
              prediksi = Array.isArray(prediksi[0]) ? prediksi[0][0] : prediksi[0];
            return {
              timestamp: item.timestamp,
              dateKey,
              class: (item.Kelas || item.kelas || item.classId || "?")
                .toString().replace("Kelas ", ""),
              suhu: toNumber(item.suhu ?? item.sensor?.suhu),
              kelembapan: toNumber(item.kelembapan ?? item.sensor?.kelembapan),
              co2: toNumber(item.co2 ?? item["gas CO₂"] ?? item.co2 ?? item.sensor?.co2),
              co: toNumber(item.co ?? item.sensor?.co),
              kebisingan: toNumber(item.kebisingan ?? item.sensor?.kebisingan),
              cahaya: toNumber(item.cahaya ?? item["intensitas cahaya"] ?? item.sensor?.cahaya),
              prediksi,
            };
          })
        );

        const uniqueClasses = [...new Set(list.map((d) => d.class))].sort();
        const uniqueMonths  = [...new Set(list.map((d) => {
          const dt = d.timestamp ? new Date(d.timestamp) : new Date(d.dateKey);
          if (isNaN(dt)) return null;
          return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}`;
        }).filter(Boolean))].sort();

        setDataPoints(list);
        setKelas(uniqueClasses);
        setBulan(uniqueMonths);
        setpilihBulan(uniqueMonths[0] || "");
        setLoading(false);
        setTimeout(() => setIsLoaded(true), 60);
      } else {
        setDataPoints([]); setKelas([]); setBulan([]);
        setLoading(false);
        setTimeout(() => setIsLoaded(true), 60);
      }
    });
    return () => unsub();
  }, []);

  // Firebase — ambang batas
  useEffect(() => {
    const unsub = onValue(ref(db, "ambangBatas"), (snap) => {
      if (snap.val()) setAmbangBatas(snap.val());
    });
    return () => unsub();
  }, []);

  // Filter
  useEffect(() => {
    const Filter = dataPoints.filter((item) => {
      const dt = item.timestamp ? new Date(item.timestamp) : new Date(item.dateKey);
      const ms = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}`;
      const fBulan = !pilihBulan || ms === pilihBulan;
      const fKelas = pilihKelas === "Semua Kelas" || item.class === pilihKelas.replace("Kelas ", "");
      return fBulan && fKelas;
    });
    setFiltered(Filter);
    setPage(1);
  }, [dataPoints, pilihBulan, pilihKelas, pilihSensor]);

  // dropdown unduh tertutup otomatis
  useEffect(() => {
    const handleClickOutside = (e) => {
        if (!menuRef.current) return;

        if (!menuRef.current.contains(e.target)) {
            setShowMenuDownload(false);
        }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
        document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // Sinkron supaya isi input ikut berubah
  useEffect(() => {
    setPageInput(page);
  }, [page]);

  // Status color
  function getSensorStatus(value, sensorKey) {
    if (value == null || !ambangBatas[sensorKey]) return "normal";
    const rules = ambangBatas[sensorKey];
    for (const level in rules) {
      const { min, max } = rules[level];
      if (value >= min && value <= max) {
        if (level.includes("bahaya")) return "danger";
        if (level.includes("waspada")) return "warning";
        if (level === "aman") return "normal";
      }
    }
    return "normal";
  }

  const statusTextClass = (s) =>
    s === "danger"  ? "text-red-600 font-semibold" :
    s === "warning" ? "text-yellow-600 font-semibold" :
                      "text-gray-700";

  // Chart
  const sensorKeys = pilihSensor === "Semua Sensor"
    ? Object.keys(SENSOR_KEYS)
    : [pilihSensor];

  // grup rata-rata per tgl
  function groupedByTanggalAvg(items) {
    const map = {};

    items.forEach((d) => {
      const day = new Date(d.timestamp).getDate().toString();
      if (!map[day]) {
        map[day] = {};
        Object.keys(SENSOR_KEYS).forEach(k => {
          map[day][k] = [];
        });
      }

      Object.keys(SENSOR_KEYS).forEach(k => {
        if (d[k] != null) {
          map[day][k].push(d[k]);
        }
      });
    });

    // hitung rata-rata
    const avgMap = {};
    Object.keys(map).forEach(day => {
      avgMap[day] = {};
      Object.keys(map[day]).forEach(sensor => {
        const arr = map[day][sensor];
        avgMap[day][sensor] = arr.length
          ? arr.reduce((a, b) => a + b, 0) / arr.length
          : null;
      });
    });

    return avgMap;
  }

  // grup rata-rata per kelas di tgl tsb
  function groupedByTanggalKelas(items) {
    const map = {};

    items.forEach((d) => {
      const day = new Date(d.timestamp).getDate().toString();
      const kelas = d.class;

      if (!map[day]) map[day] = {};
      if (!map[day][kelas]) {
        map[day][kelas] = {};
        Object.keys(SENSOR_KEYS).forEach(k => {
          map[day][kelas][k] = [];
        });
      }

      Object.keys(SENSOR_KEYS).forEach(k => {
        if (d[k] != null) {
          map[day][kelas][k].push(d[k]);
        }
      });
    });

    // hitung rata-rata
    const avgMap = {};
    Object.keys(map).forEach(day => {
      avgMap[day] = {};
      Object.keys(map[day]).forEach(kelas => {
        avgMap[day][kelas] = {};
        Object.keys(map[day][kelas]).forEach(sensor => {
          const arr = map[day][kelas][sensor];
          avgMap[day][kelas][sensor] = arr.length
            ? arr.reduce((a, b) => a + b, 0) / arr.length
            : null;
        });
      });
    });

    return avgMap;
  }

  // grup rata-rata per bulan
  function groupedByBulanAvg(items) {
    const map = {};

    items.forEach((d) => {
      const dt = new Date(d.timestamp);
      const key = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}`;

      if (!map[key]) {
        map[key] = {};
        Object.keys(SENSOR_KEYS).forEach(k => {
          map[key][k] = [];
        });
      }

      Object.keys(SENSOR_KEYS).forEach(k => {
        if (d[k] != null) {
          map[key][k].push(d[k]);
        }
      });
    });

    const avgMap = {};
    Object.keys(map).forEach(bulan => {
      avgMap[bulan] = {};
      Object.keys(map[bulan]).forEach(sensor => {
        const arr = map[bulan][sensor];
        avgMap[bulan][sensor] = arr.length
          ? arr.reduce((a, b) => a + b, 0) / arr.length
          : null;
      });
    });

    return avgMap;
  }

  // grup rata-rata per kelas di bulan tsb
  function groupedByBulanKelas(items) {
    const map = {};

    items.forEach((d) => {
      const dt = new Date(d.timestamp);
      if (isNaN(dt)) return;

      const key = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}`;
      const kelas = d.class;

      if (!map[key]) map[key] = {};
      if (!map[key][kelas]) {
        map[key][kelas] = {};
        Object.keys(SENSOR_KEYS).forEach(k => {
          map[key][kelas][k] = [];
        });
      }

      Object.keys(SENSOR_KEYS).forEach(k => {
        if (d[k] != null) {
          map[key][kelas][k].push(d[k]);
        }
      });
    });

    // hitung rata-rata
    const avgMap = {};
    Object.keys(map).forEach(bulan => {
      avgMap[bulan] = {};
      Object.keys(map[bulan]).forEach(kelas => {
        avgMap[bulan][kelas] = {};
        Object.keys(map[bulan][kelas]).forEach(sensor => {
          const arr = map[bulan][kelas][sensor];
          avgMap[bulan][kelas][sensor] = arr.length
            ? arr.reduce((a, b) => a + b, 0) / arr.length
            : null;
        });
      });
    });

    return avgMap;
  }

  const semuaBulan = !pilihBulan;
  const groupedTanggal = groupedByTanggalAvg(filtered);
  const groupedBulan = groupedByBulanAvg(filtered);
  
  const grouped = semuaBulan ? groupedByBulanAvg(filtered) : groupedByTanggalAvg(filtered);
  const groupedByKelas = groupedByTanggalKelas(filtered);
  const fixedLabels = semuaBulan ? bulan : Array.from({length: 31}, (_, i) => (i + 1).toString());
  const displayLabels = semuaBulan
    ? bulan.map(m => {
        const [y, mo] = m.split("-");
        return new Date(y, mo - 1).toLocaleString("id-ID", {
          month: "short",
          year: "numeric"
        });
      })
    : fixedLabels;

  const chartData = {
    labels: displayLabels,
    datasets: sensorKeys.map((key) => ({
      label: `${SENSOR_KEYS[key].label} (${SENSOR_KEYS[key].unit})`,
      data: fixedLabels.map((label) => grouped[label]?.[key] ?? null),
      borderColor: SENSOR_KEYS[key].color,
      backgroundColor: SENSOR_KEYS[key].color + "22",
      tension: 0.4,
      spanGaps: true,
      pointRadius: 3,
      pointHoverRadius:5,
      borderWidth: 2,
    })),
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top",
        labels: {usePointStyle: true, pointStyle: "circle", font: {size: 12}, padding: 16},
      },
      tooltip: {
        backgroundColor: "#1e293b",
        titleColor: "#94a3b8",
        bodyColor: "#f1f5f9",
        padding: 10,
        cornerRadius: 8,
        callbacks: {
          label(ctx) {
            const index = ctx.dataIndex;
            const key = fixedLabels[index];
            const sKey = sensorKeys[ctx.datasetIndex];
            const unit = SENSOR_KEYS[sKey].unit;
            const avg = grouped[key]?.[sKey];

            //nilai rata-rata global
            let lines = [];

            if (avg != null) {
              lines.push(`Rata-rata: ${avg.toFixed(1)} ${unit}`);
            }

            // PER KELAS (TERMASUK SEMUA BULAN)
            if (pilihKelas === "Semua Kelas") {

              const kelasData = semuaBulan
                ? groupedByBulanKelas(filtered)[key]   // 🔥 BULAN
                : groupedByTanggalKelas(filtered)[key]; // 🔥 HARI

              if (kelasData) {
                Object.keys(kelasData).forEach(kelas => {
                  const val = kelasData[kelas][sKey];
                  if (val != null) {
                    lines.push(`• Kelas ${kelas}: ${val.toFixed(1)} ${unit}`);
                  }
                });
              }
            }
            // filter: semua kelas + semua sensor
            // if (!semuaBulan && pilihKelas === "Semua Kelas") {
            //   const kelasData = groupedByKelas[label];
              // if (!kelasData) return "Tidak ada data";

              // const lines = [];

              // if (pilihSensor === "Semua Sensor") {
              //   lines.push(`${SENSOR_KEYS[sKey].label}:`);
              // }

              // Object.keys(kelasData).forEach(kelas => {
              //   const val = kelasData[kelas][sKey];
              //   if (val != null) {
              //     lines.push(`Kelas ${kelas}: ${val.toFixed(1)} ${unit}`);
              //   }
              // });

            //   if (kelasData) {
            //     Object.keys(kelasData).forEach(kelas => {
            //       const val = kelasData[kelas][sKey];
            //       if (val != null) {
            //         lines.push(`•	Kelas ${kelas}: ${val.toFixed(1)} ${unit}`);
            //       }
            //     });
            //   }
            //   return lines;
            // }
            // const nilai = grouped[day]?.[sKey];
            // return `${SENSOR_KEYS[sKey].label}: ${nilai ? nilai.toFixed(1): "-"} ${unit}`;
            return lines.length ? lines : "Tidak ada data";
          },
        },
      },
    },
    scales: {
      x: {
        grid: {color: "#f1f5f9"},
        ticks: {color: "#94a3b8", font: {size: 11}},
      },
      y: {
        grid: {color: "#f1f5f9"},
        ticks: {color: "#94a3b8", font: {size: 11}},
      },
    },
  };

  // Page/jumlah page
  const totalPages = Math.max(1, Math.ceil(filtered.length / rowsPerPage));
  const paginated  = filtered.slice((page - 1) * rowsPerPage, page * rowsPerPage);
  const handlePageChange = () => {
    let value = Number(pageInput);

    if (isNaN(value)) value = 1;

    if (value < 1) value = 1;
    if (value > totalPages) value = totalPages;

    setPage(value);
    setPageInput(value);
  };

  // Summary stats
  const avgOf = (key) => {
    const vals = filtered.map((d) => d[key]).filter((v) => v != null);
    return vals.length ? (vals.reduce((a, b) => a + b, 0) / vals.length).toFixed(1) : "-";
  };

  // Download PDF
  const downloadPDF = () => {
    if (!rows.length) { 
      alert("Tidak ada data untuk diunduh."); 
      return;
    }

    const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
    doc.setFontSize(14);
    doc.text("Data Historis Pemantauan Lingkungan", 14, 14);
    doc.setFontSize(10);
    doc.text(`Periode: ${labelBulan(pilihBulan)}   |   Filter Kelas: ${pilihKelas}`, 14, 21);

    autoTable(doc, {
      head: [["Tanggal","Ruangan","Suhu (°C)","Kelembapan (%)","Gas CO2 (ppm)","Asap (ppm)","Kebisingan (dB)"," Intensitas Cahaya (lux)","Status"]],
      body: rows.map((d) => [
        (() => {try {const dd = new Date(d.timestamp); return isNaN(dd) ? d.timestamp : dd.toLocaleDateString("id-ID");} catch {return d.timestamp;}})(),
        `Kelas ${d.class}`,
        d.suhu?.toFixed(1) ?? "-",
        d.kelembapan?.toFixed(1) ?? "-",
        d.co2?.toFixed(1) ?? "-",
        d.co?.toFixed(1) ?? "-",
        d.kebisingan?.toFixed(1) ?? "-",
        d.cahaya?.toFixed(1) ?? "-",
        prediksiLabel(d.prediksi).text,
      ]),
      startY: 26,
      styles: {fontSize: 9},
      headStyles: {fillColor: [30, 64, 175]},
    });
    doc.save(`DataHistoris_${labelBulan(pilihBulan).replace(/\s+/g, "_")}.pdf`);
  };

  const downloadCSV = () => {
    if (!rows.length) {
      alert("Tidak ada data");
      return;
    }

    const headers = [
      "Tanggal", "Ruangan", "Suhu", "Kelembapan", "CO", "CO2", "Cahaya", "Kebisingan", "Status"
    ];

    const body = rows.map((d) => [
      new Date(d.timestamp).toLocaleString(),
      d.Kelas,
      d.suhu,
      d.kelembapan,
      d.co,
      d.co2,
      d.cahaya,
      d.kebisingan,
      d.prediksiLabel
    ]);

    const csvContent = [
      headers.join(","),
      ...body.map(r => r.join(","))
    ].join("\n");

    const blob = new Blob([csvContent], {
      type: "text/csv;charset=utf-8;"
    });

    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `DataHistoris_${labelBulan(pilihBulan).replace(/\s+/g, "_")}.csv`;

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
          <p className="text-sm text-slate-500 font-medium">Memuat data historis…</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen bg-slate-50 transition-all duration-500 ${
        isLoaded ? "opacity-100" : "opacity-0"
      }`}
    >
      <Navbar />

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">

        {/* Page header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-slate-800 tracking-tight">
              Data Historis
            </h1>
            <p className="text-sm text-slate-500 mt-0.5">
              Pemantauan Lingkungan Kelas
            </p>
          </div>
        </div>

        {/* Filter bar */}
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-4 flex flex-wrap items-center gap-3">
          <Filter size={16} className="text-slate-400 flex-shrink-0" />
          <span className="text-sm font-medium text-slate-500 mr-1">Filter:</span>

          {/* Bulan */}
          <select
            className="border border-slate-200 bg-slate-50 rounded-lg px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-emerald-400"
            value={pilihBulan}
            onChange={(e) => setpilihBulan(e.target.value)}
          >
            <option value="">Semua Bulan</option>
            {bulan.map((m) => {
              const [y, mo] = m.split("-");
              const label = new Date(Number(y), Number(mo) - 1)
                .toLocaleString("id-ID", { month: "long", year: "numeric" });
              return <option key={m} value={m}>{label}</option>;
            })}
          </select>

          {/* Kelas */}
          <select
            className="border border-slate-200 bg-slate-50 rounded-lg px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-emerald-400"
            value={pilihKelas}
            onChange={(e) => setpilihKelas(e.target.value)}
          >
            <option>Semua Kelas</option>
            {kelas.map((c) => (
              <option key={c} value={c}>Kelas {c}</option>
            ))}
          </select>

          {/* Sensor */}
          <select
            className="border border-slate-200 bg-slate-50 rounded-lg px-3 py-2 text-sm text-slate-700 focus:outline-none focus:ring-2 focus:ring-emerald-400"
            value={pilihSensor}
            onChange={(e) => setpilihSensor(e.target.value)}
          >
            <option value="Semua Sensor">Semua Sensor</option>
            {Object.entries(SENSOR_KEYS).map(([key, { label }]) => (
              <option key={key} value={key}>{label}</option>
            ))}
          </select>

          <span className="ml-auto text-xs text-slate-400">
            {filtered.length} data ditemukan
          </span>
        </div>

        {/* Chart */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
          <div className="flex items-center gap-2 mb-5">
            <BarChart2 size={18} className="text-blue-600" />
            <h2 className="text-xl text-base font-bold text-slate-700">
              Grafik Data Sensor — {labelBulan(pilihBulan)}
            </h2>
          </div>
          <div className="h-64">
            <Line data={chartData} options={chartOptions} />
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
          <div className="flex text-sm items-center justify-between px-2 py-4">
            <h2 className="text-base font-bold text-slate-700">
              Tabel Data Historis
            </h2>

            <div className="relative" 
              ref={menuRef} 
              onClick={() => setShowMenuDownload(prev => !prev)}
            >
              <button
                className={`flex items-center gap-2 bg-primaryDark hover:bg-primary text-white px-4 py-2 transition
                ${showMenuDownload ? "rounded-t-xl rounded-b-none" : "rounded-xl"}`}
              >
                <Download size={15} />
                <span>Unduh</span>
                <ChevronDown size={16}/>
              </button>

              <div className={`absolute top-full right-0 w-38 bg-white rounded-b-xl shadow-xl border z-38 overflow-hidden transition-all duration-200 origin-top-right 
                ${showMenuDownload ? "opacity-100 scale-100 translate-y-0" : "opacity-0 scale-95 pointer-events-none"}`}>
                <button
                  onClick={() => {
                    downloadPDF();
                    setShowMenuDownload(false);
                  }}
                  className="w-full text-left px-4 py-2 text-white bg-primaryDark hover:bg-primary flex items-center gap-2"
                >
                  <FileText size={16} />
                  Unduh PDF
                </button>

                <button
                  onClick={() => {
                    downloadCSV();
                    setShowMenuDownload(false);
                  }}
                  className="w-full text-left px-4 py-2 text-white bg-primaryDark hover:bg-primary flex items-center gap-2"
                >
                  <FileSpreadsheet size={16} />
                  Unduh CSV
                </button>
              </div>
            </div>
          </div>

          <div className="overflow-x-auto rounded-xl border border-slate-100">
            <table className="w-full text-sm text-slate-700 border-collapse">
              <thead>
                <tr className="bg-slate-50 text-xs text-slate-500 uppercase tracking-wider">
                  {[
                    "Tanggal", "Ruangan",
                    "Suhu", "Kelembapan", "Gas CO₂",
                    "Asap", "Kebisingan", "Cahaya", "Status",
                  ].map((h) => (
                    <th key={h} className="px-4 py-3 text-left font-semibold border-b border-slate-100 whitespace-nowrap">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {paginated.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="px-4 py-10 text-center text-slate-400 text-sm">
                      Tidak ada data untuk filter yang dipilih.
                    </td>
                  </tr>
                ) : (
                  paginated.map((d, i) => {
                    const { text: pText, cls: pCls } = prediksiLabel(d.prediksi);
                    return (
                      <tr key={i} className="hover:bg-slate-50 transition-colors">
                        <td className="px-4 py-3 text-slate-500 whitespace-nowrap">
                          {new Date(d.timestamp).toLocaleDateString("id-ID")}
                        </td>
                        <td className="px-4 py-3 font-medium text-slate-800 whitespace-nowrap">
                          Kelas {d.class}
                        </td>
                        <td className={`px-4 py-3 whitespace-nowrap ${statusTextClass(getSensorStatus(d.suhu, "suhu"))}`}>
                          {d.suhu != null ? d.suhu.toFixed(1) + " °C" : "–"}
                        </td>
                        <td className={`px-4 py-3 whitespace-nowrap ${statusTextClass(getSensorStatus(d.kelembapan, "kelembapan"))}`}>
                          {d.kelembapan != null ? d.kelembapan.toFixed(1) + " %" : "–"}
                        </td>
                        <td className={`px-4 py-3 whitespace-nowrap ${statusTextClass(getSensorStatus(d.co2, "co2"))}`}>
                          {d.co2 != null ? d.co2.toFixed(1) + " ppm" : "–"}
                        </td>
                        <td className={`px-4 py-3 whitespace-nowrap ${statusTextClass(getSensorStatus(d.co, "co"))}`}>
                          {d.co != null ? d.co.toFixed(1) + " ppm" : "–"}
                        </td>
                        <td className={`px-4 py-3 whitespace-nowrap ${statusTextClass(getSensorStatus(d.kebisingan, "kebisingan"))}`}>
                          {d.kebisingan != null ? d.kebisingan.toFixed(1) + " dB" : "–"}
                        </td>
                        <td className={`px-4 py-3 whitespace-nowrap ${statusTextClass(getSensorStatus(d.cahaya, "cahaya"))}`}>
                          {d.cahaya != null ? d.cahaya.toFixed(1) + " lux" : "–"}
                        </td>
                        <td className="px-4 py-3">
                          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold ${pCls}`}>
                            {pText}
                          </span>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between mt-5">
            <p className="text-xs text-slate-400">
              Menampilkan {Math.min((page - 1) * rowsPerPage + 1, filtered.length)}–
              {Math.min(page * rowsPerPage, filtered.length)} dari {filtered.length} data
            </p>
            <div className="flex items-center gap-2">
              <button
                disabled={page === 1}
                onClick={() => setPage(page - 1)}
                className="flex items-center gap-1 px-3 py-1.5 rounded-lg border border-slate-200 text-sm font-medium text-slate-600 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-slate-100 transition-colors"
              >
                <ChevronLeft size={14} />
              </button>
              <input
                type="number"
                min={1}
                max={totalPages}
                value={pageInput}
                onChange={(e) => setPageInput(e.target.value)}
                onBlur={handlePageChange}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handlePageChange();
                  }
                }}
                className="w-16 text-center border border-slate-300 rounded-lg py-1.5 text-sm font-semibold focus:ring-2 focus:ring-emerald-400 focus:border-emerald-400 outline-none"
              />
              <span className="text-sm font-semibold text-slate-700 min-w-[60px] text-center">
                / {totalPages}
              </span>
              <button
                disabled={page === totalPages}
                onClick={() => setPage(page + 1)}
                className="flex items-center gap-1 px-3 py-1.5 rounded-lg border border-slate-200 text-sm font-medium text-slate-600 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-slate-100 transition-colors"
              >
               <ChevronRight size={14} />
              </button>
            </div>
          </div>
        </div>

      </main>
    </div>
  );
}